import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";

interface City {
  name: string;
  displayName: string;
  postalCode?: string;
}

// Country codes for Nominatim API
const COUNTRY_CODES: Record<string, string> = {
  "France": "fr",
  "Belgique": "be",
  "Espagne": "es",
  "Allemagne": "de",
  "Italie": "it",
  "Pays-Bas": "nl",
  "Maroc": "ma",
};

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
  country?: string;
}

export function CityAutocomplete({
  value,
  onChange,
  placeholder = "Rechercher une ville...",
  className,
  id,
  disabled = false,
  country = "France",
}: CityAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<City[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Sync external value
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Reset suggestions when country changes
  useEffect(() => {
    setSuggestions([]);
  }, [country]);

  // Fetch cities from appropriate API based on country
  useEffect(() => {
    const fetchCities = async () => {
      if (inputValue.length < 2) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        let cities: City[] = [];

        if (country === "France") {
          // Use French government API for France
          const response = await fetch(
            `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(inputValue)}&fields=nom,code,codeDepartement,codesPostaux,population&boost=population&limit=8`
          );
          if (response.ok) {
            const data = await response.json();
            cities = data.map((city: any) => ({
              name: city.nom,
              displayName: city.nom,
              postalCode: city.codesPostaux?.[0] || city.codeDepartement,
            }));
          }
        } else {
          // Use Nominatim API for other countries
          const countryCode = COUNTRY_CODES[country] || "fr";
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(inputValue)}&countrycodes=${countryCode}&format=json&addressdetails=1&limit=8&featuretype=city`,
            {
              headers: {
                "Accept-Language": "fr",
              },
            }
          );
          if (response.ok) {
            const data = await response.json();
            // Filter to only get cities/towns and remove duplicates
            const seen = new Set<string>();
            cities = data
              .filter((item: any) => {
                const cityName = item.address?.city || item.address?.town || item.address?.village || item.address?.municipality || item.name;
                if (!cityName || seen.has(cityName.toLowerCase())) return false;
                seen.add(cityName.toLowerCase());
                return true;
              })
              .map((item: any) => {
                const cityName = item.address?.city || item.address?.town || item.address?.village || item.address?.municipality || item.name;
                const postalCode = item.address?.postcode;
                return {
                  name: cityName,
                  displayName: cityName,
                  postalCode: postalCode,
                };
              });
          }
        }

        setSuggestions(cities);
      } catch (error) {
        console.error("Error fetching cities:", error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchCities, 300);
    return () => clearTimeout(debounceTimer);
  }, [inputValue, country]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    setShowSuggestions(true);
    setHighlightedIndex(-1);
  };

  const handleSelectCity = (city: City) => {
    const cityName = city.name;
    setInputValue(cityName);
    onChange(cityName);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelectCity(suggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          id={id}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => inputValue.length >= 2 && setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn("pr-8", className)}
          disabled={disabled}
          autoComplete="off"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg overflow-hidden"
        >
          {suggestions.map((city, index) => (
            <button
              key={`${city.name}-${index}`}
              type="button"
              onClick={() => handleSelectCity(city)}
              className={cn(
                "w-full px-3 py-2.5 text-left flex items-center gap-2 hover:bg-accent transition-colors",
                highlightedIndex === index && "bg-accent"
              )}
            >
              <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="font-medium text-foreground">{city.displayName}</span>
                {city.postalCode && (
                  <span className="text-sm text-muted-foreground ml-2">
                    ({city.postalCode})
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {showSuggestions && inputValue.length >= 2 && suggestions.length === 0 && !isLoading && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg p-3"
        >
          <p className="text-sm text-muted-foreground text-center">
            Aucune ville trouvée
          </p>
        </div>
      )}
    </div>
  );
}
